import { useState, useEffect, useRef, useCallback } from 'react';
import { AgentExecutionsSSE, AgentExecutionCallbacks } from '../events/AgentExecuted';
import { AgentExecution } from '../types/AgentExecution';
import { SSEConnection } from '@/shared/utils/sseConnection';

interface UseAgentExecutionsSSEOptions {
  agentBaseId?: string;
  executionId?: string;
  initialExecutions?: AgentExecution[];
}

interface UseAgentExecutionsSSEResult {
  executions: AgentExecution[];
  isConnected: boolean;
  error: Error | null;
  reconnect: () => void;
  disconnect: () => void;
}

/**
 * Hook para gerenciar conexões SSE para execuções de agentes
 */
export function useAgentExecutionsSSE({
  agentBaseId,
  executionId,
  initialExecutions = []
}: UseAgentExecutionsSSEOptions): UseAgentExecutionsSSEResult {
  const [executions, setExecutions] = useState<AgentExecution[]>(initialExecutions);

  // Este efeito garante que quando initialExecutions mudar, atualizamos o estado local
  useEffect(() => {
    console.log('[useAgentExecutionsSSE] initialExecutions atualizado:', initialExecutions);
    if (initialExecutions && initialExecutions.length > 0) {
      console.log('[useAgentExecutionsSSE] Atualizando estado de executions com initialExecutions');
      setExecutions(initialExecutions);
    }
  }, [initialExecutions]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Referência para o EventSource e SSEConnection
  const eventSourceRef = useRef<EventSource | null>(null);
  const sseConnectionRef = useRef<SSEConnection | null>(null);
  const maxReconnectAttempts = 5;
  const reconnectAttemptsRef = useRef(0);

  // Função para atualizar uma execução existente ou adicionar uma nova
  const updateOrAddExecution = useCallback((updatedExecution: AgentExecution) => {
    console.log('[useAgentExecutionsSSE] Atualizando ou adicionando execução:', updatedExecution);

    setExecutions(prevExecutions => {
      // Verifica se a execução já existe no array
      const existingIndex = prevExecutions.findIndex(exec => exec.id === updatedExecution.id);

      if (existingIndex !== -1) {
        console.log(`[useAgentExecutionsSSE] Atualizando execução existente no índice ${existingIndex}`);
        const existingExecution = prevExecutions[existingIndex];

        // Atualiza a execução existente com merge inteligente
        const updatedExecutions = [...prevExecutions];
        updatedExecutions[existingIndex] = {
          ...existingExecution,
          ...updatedExecution,
          // Preserva completed_at se já existir e não estiver sendo sobrescrito explicitamente
          completed_at: updatedExecution.completed_at !== undefined
            ? updatedExecution.completed_at
            : existingExecution.completed_at
        };

        console.log(`[useAgentExecutionsSSE] Execução atualizada:`, updatedExecutions[existingIndex]);
        return updatedExecutions;
      } else {
        console.log('[useAgentExecutionsSSE] Adicionando nova execução ao início da lista');
        // Adiciona nova execução no início do array
        return [updatedExecution, ...prevExecutions];
      }
    });
  }, []);

  // Gerencia o ciclo de vida da conexão SSE
  useEffect(() => {
    // Não fazer nada se não temos ID para conectar
    if (!agentBaseId && !executionId) return;

    console.log(`[useAgentExecutionsSSE] Configurando callbacks para ${agentBaseId ? 'agente' : 'execução'} ${agentBaseId || executionId}`);

    // Configura os callbacks para os eventos
    const callbacks: AgentExecutionCallbacks = {
      onConnect: () => {
        console.log('[useAgentExecutionsSSE] Conexão estabelecida com sucesso');
        setIsConnected(true);
        setError(null);
      },

      onExecutionStarted: (data) => {
        console.log('[useAgentExecutionsSSE] Nova execução iniciada:', data);
        // Adiciona nova execução ao estado
        const newExecution: AgentExecution = {
          id: data.executionId ?? data.id ?? '',
          agent_base_id: data.agentId ?? data.agent_base_id ?? '',
          status: 'running',
          started_at: data.timestamp ?? data.started_at ?? new Date().toISOString(),
          completed_at: null,
          error_message: null,
          metadata: null,
          created_at: data.created_at ?? new Date().toISOString(),
          updated_at: data.updated_at ?? new Date().toISOString()
        };
        updateOrAddExecution(newExecution);

        // Força uma re-renderização após adicionar uma nova execução
        setTimeout(() => {
          setExecutions(prev => [...prev]);
        }, 0);
      },

      onExecutionProgress: (data) => {
        console.log('[useAgentExecutionsSSE] Progresso na execução:', data);
        // Atualiza o status da execução existente
        const progressUpdate: Partial<AgentExecution> = {
          id: data.executionId ?? data.id ?? '',
          status: 'running',
          updated_at: data.timestamp ?? new Date().toISOString()
        };

        if (data.progress !== undefined) {
          if (!progressUpdate.metadata) progressUpdate.metadata = {};
          progressUpdate.metadata.progress = data.progress;
        }

        updateOrAddExecution(progressUpdate as AgentExecution);
      },

      onExecutionCompleted: (data) => {
        console.log('[useAgentExecutionsSSE] Execução concluída:', data);
        // Atualiza a execução para mostrar que foi concluída
        const completedUpdate: Partial<AgentExecution> = {
          id: data.executionId ?? data.id ?? '',
          status: 'completed',
          completed_at: data.timestamp ?? data.completed_at ?? new Date().toISOString(),
          updated_at: data.timestamp ?? new Date().toISOString()
        };

        if (data.result) {
          if (!completedUpdate.metadata) completedUpdate.metadata = {};
          completedUpdate.metadata.result = data.result;
        }

        updateOrAddExecution(completedUpdate as AgentExecution);
      },

      onExecutionFailed: (data) => {
        console.log('[useAgentExecutionsSSE] Execução falhou:', data);
        // Atualiza a execução para mostrar que falhou
        const failedUpdate: Partial<AgentExecution> = {
          id: data.executionId ?? data.id ?? '',
          status: 'error',
          completed_at: data.timestamp ?? data.completed_at ?? new Date().toISOString(),
          error_message: data.error_message ?? (data.message ? String(data.message) : null) ?? 'Erro desconhecido',
          updated_at: data.timestamp ?? new Date().toISOString()
        };
        updateOrAddExecution(failedUpdate as AgentExecution);
      },

      onNewExecution: (data) => {
        console.log('[useAgentExecutionsSSE] Nova execução (new-execution):', data);
        // Trata como uma nova execução - o backend envia o objeto completo
        const newExecution: AgentExecution = {
          id: data.id ?? data.executionId ?? '',
          agent_base_id: data.agent_base_id ?? data.agentId ?? '',
          agent_version_id: data.agent_version_id,
          status: data.status ?? 'running',
          started_at: data.started_at ?? data.timestamp ?? new Date().toISOString(),
          created_at: data.created_at ?? data.timestamp ?? new Date().toISOString(),
          updated_at: data.updated_at ?? data.timestamp ?? new Date().toISOString(),
          completed_at: data.completed_at ?? null,
          error_message: data.error_message ?? null,
          metadata: (data.metadata as AgentExecution['metadata']) ?? null,
          version_info: (data.version_info as { name: string; version: string } | undefined) ?? null
        };
        console.log('[useAgentExecutionsSSE] Execução processada para adição:', newExecution);
        updateOrAddExecution(newExecution);
      },

      onExecutionUpdated: (data) => {
        console.log('[useAgentExecutionsSSE] Execução atualizada (execution-updated):', data);
        // Atualiza execução existente com novos dados
        const executionUpdate: Partial<AgentExecution> = {
          id: data.executionId ?? data.id ?? '',
          updated_at: data.timestamp ?? data.updated_at ?? new Date().toISOString()
        };

        // Adiciona campos que estão presentes nos dados
        if (data.status) {
          executionUpdate.status = data.status;
          // Se o status indica finalização, define completed_at se não existir
          if ((data.status === 'completed' || data.status === 'error' || data.status === 'failed') && !data.completed_at) {
            executionUpdate.completed_at = data.timestamp ?? new Date().toISOString();
            console.log(`[useAgentExecutionsSSE] Status finalizado (${data.status}) em execution-updated, definindo completed_at:`, executionUpdate.completed_at);
          }
        }
        if (data.progress !== undefined) {
          if (!executionUpdate.metadata) executionUpdate.metadata = {};
          executionUpdate.metadata.progress = data.progress;
        }
        if (data.error || data.error_message) executionUpdate.error_message = data.error_message ?? (data.error ? String(data.error) : null);
        if (data.result) executionUpdate.metadata = { ...executionUpdate.metadata, result: data.result };
        if (data.completed_at) executionUpdate.completed_at = data.completed_at;

        updateOrAddExecution(executionUpdate as AgentExecution);
      },

      onStatusUpdate: (data) => {
        console.log('[useAgentExecutionsSSE] Status atualizado (status):', data);
        // Atualiza apenas o status da execução
        const statusUpdate: Partial<AgentExecution> = {
          id: data.executionId ?? data.id ?? '',
          status: data.status ?? 'running',
          updated_at: data.timestamp ?? data.updated_at ?? new Date().toISOString()
        };

        // Se o status é completed ou error, adiciona completed_at se não existir
        if (data.status === 'completed' || data.status === 'error' || data.status === 'failed') {
          statusUpdate.completed_at = data.timestamp ?? data.completed_at ?? new Date().toISOString();
          console.log(`[useAgentExecutionsSSE] Status finalizado (${data.status}), definindo completed_at:`, statusUpdate.completed_at);
        }

        updateOrAddExecution(statusUpdate as AgentExecution);
      },

      onInit: (data) => {
        console.log('[useAgentExecutionsSSE] Dados iniciais recebidos (init):', data);
        // Se o init contém uma lista de execuções, podemos atualizar o estado
        if (Array.isArray(data)) {
          console.log('[useAgentExecutionsSSE] Atualizando com dados iniciais do SSE');
          setExecutions(data);
        } else if (data.executions && Array.isArray(data.executions)) {
          console.log('[useAgentExecutionsSSE] Atualizando com execuções do objeto init');
          setExecutions(data.executions);
        }
      },

      onError: (event) => {
        console.error('[useAgentExecutionsSSE] Erro na conexão SSE:', event);
        setError(new Error('Erro na conexão com o servidor'));
        setIsConnected(false);
      },

      // Handler genérico para qualquer outro evento
      onMessage: (data) => {
        console.log('[useAgentExecutionsSSE] Evento genérico recebido:', data);

        // Processar qualquer tipo de evento que tenha ID de execução e alguma informação de status
        if (data && data.executionId) {
          console.log('[useAgentExecutionsSSE] Processando evento genérico como atualização de execução');

          const execId = String(data.executionId);
          const execTimestamp = data.timestamp ? String(data.timestamp) : undefined;
          const execStatus = data.status ? String(data.status) : undefined;
          const execType = data.type ? String(data.type) : undefined;

          // Constrói objeto de execução com os dados disponíveis
          const executionUpdate: Partial<AgentExecution> = {
            id: execId,
          };

          // Se tiver timestamp e não tiver completed_at, usar como started_at
          if (execTimestamp && !data.completed_at) {
            executionUpdate.started_at = execTimestamp;
          }

          // Se tiver timestamp e tiver completed ou similar no status, usar como completed_at
          if (execTimestamp && (execStatus?.includes('complete') || execStatus === 'finished' || execStatus === 'done')) {
            executionUpdate.completed_at = execTimestamp;
          }

          // Define o status baseado no campo status ou type do evento
          if (execStatus) {
            executionUpdate.status = execStatus;
          } else if (execType) {
            if (execType.includes('completed') || execType.includes('finish')) {
              executionUpdate.status = 'completed';
            } else if (execType.includes('failed') || execType.includes('error')) {
              executionUpdate.status = 'error';
            } else if (execType.includes('start')) {
              executionUpdate.status = 'running';
            }
          }

          // Se tem progresso, adicionar como metadata
          if (data.progress !== undefined) {
            if (!executionUpdate.metadata) {
              executionUpdate.metadata = {};
            }
            executionUpdate.metadata.progress = data.progress;
          }

          // Adiciona info do agente se disponível
          if (data.agentId) {
            executionUpdate.agent_base_id = String(data.agentId);
          }

          // Atualiza a execução se tiver pelo menos id e alguma outra informação útil
          if (executionUpdate.id && (
            executionUpdate.status ||
            executionUpdate.started_at ||
            executionUpdate.completed_at ||
            executionUpdate.metadata?.progress
          )) {
            console.log('[useAgentExecutionsSSE] Atualizando execução a partir de evento genérico:', executionUpdate);
            updateOrAddExecution(executionUpdate as AgentExecution);

            // Força atualização imediata
            setTimeout(() => {
              setExecutions(prev => [...prev]);
            }, 0);
          }
        }
      }
    };

    // Estabelece a conexão SSE apropriada baseada nos parâmetros
    const connect = () => {
      try {
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }

        // Enhanced callbacks with retry tracking
        const enhancedCallbacks: AgentExecutionCallbacks = {
          ...callbacks,
          onConnect: (connectionData) => {
            setIsConnected(true);
            setError(null);
            reconnectAttemptsRef.current = 0;
            callbacks.onConnect?.(connectionData);
          },
          onError: (event) => {
            setIsConnected(false);

            // Check if we should retry
            if (reconnectAttemptsRef.current < maxReconnectAttempts) {
              reconnectAttemptsRef.current++;
              const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current - 1), 30000);


              setTimeout(() => {
                connect();
              }, delay);
            } else {
              setError(new Error('Não foi possível estabelecer conexão após múltiplas tentativas'));
            }

            callbacks.onError?.(event);
          }
        };

        if (executionId) {
          console.log(`[useAgentExecutionsSSE] Conectando à execução específica: ${executionId}`);
          eventSourceRef.current = AgentExecutionsSSE.connectToExecution(executionId, enhancedCallbacks);
        } else if (agentBaseId) {
          console.log(`[useAgentExecutionsSSE] Conectando às execuções do agente: ${agentBaseId}`);
          eventSourceRef.current = AgentExecutionsSSE.connectToAgentExecutions(agentBaseId, enhancedCallbacks);
        } else {
          console.log('[useAgentExecutionsSSE] Conectando a todas as execuções');
          eventSourceRef.current = AgentExecutionsSSE.connectToAllExecutions(enhancedCallbacks);
        }
      } catch (err) {
        console.error('[useAgentExecutionsSSE] Erro ao estabelecer conexão:', err);
        setError(err instanceof Error ? err : new Error('Erro ao conectar ao servidor'));
        setIsConnected(false);
      }
    };

    // Inicia a conexão
    connect();

    // Monitora visibilidade da página para reconectar quando o usuário volta
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && eventSourceRef.current?.readyState === EventSource.CLOSED) {
        console.log('[useAgentExecutionsSSE] Página visível novamente, reconectando...');
        connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup: fecha a conexão e remove event listeners
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if (eventSourceRef.current) {
        console.log('[useAgentExecutionsSSE] Fechando conexão no cleanup');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [agentBaseId, executionId, updateOrAddExecution]);

  // Função para reconectar manualmente
  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0; // Reset retry counter on manual reconnect

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    if (sseConnectionRef.current) {
      sseConnectionRef.current.disconnect(true);
      sseConnectionRef.current = null;
    }

    // Trigger reconnection through the effect
    setError(null);
    setIsConnected(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentBaseId, executionId]);

  // Função para desconectar manualmente
  const disconnect = useCallback(() => {
    console.log('[useAgentExecutionsSSE] Desconectando manualmente...');
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setIsConnected(false);
    }
  }, []);

  return {
    executions,
    isConnected,
    error,
    reconnect,
    disconnect
  };
}
